import { useAsync, useMountEffect } from "@react-hookz/web";
import { type FunctionComponent, useEffect, useMemo } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import type { Props as VideoUpNextWidgetProps } from "scmp-app/components/video/video-up-next-widget";
import { VideoUpNextWidget } from "scmp-app/components/video/video-up-next-widget";
import type { upNextWidgetSectionQuery } from "scmp-app/queries/__generated__/upNextWidgetSectionQuery.graphql";

type Props = Omit<VideoUpNextWidgetProps, "reference"> & {
  onSyncVideos: (videos: string[][]) => void;
  sectionId: string;
};

export const UpNextWidget: FunctionComponent<Props> = ({ onSyncVideos, sectionId, ...props }) => {
  const environment = useRelayEnvironment();
  const [{ result }, { execute }] = useAsync(async () => {
    const data = await fetchQuery<upNextWidgetSectionQuery>(
      environment,
      graphql`
        query upNextWidgetSectionQuery($queueName: String!) {
          queue(filter: { name: $queueName }) {
            items(first: 30) {
              edges {
                node {
                  ... on Video {
                    entityId
                    urlAlias
                  }
                }
                ...videoUpNextWidgetVideo
              }
            }
          }
        }
      `,
      {
        queueName: `video_section_picks_${sectionId}`,
      },
      {
        fetchPolicy: "store-or-network",
      },
    ).toPromise();

    if (!data?.queue?.items?.edges) return;
    return data.queue.items.edges;
  });
  useMountEffect(execute);

  const videos = useMemo(
    () => result?.map(({ node: { entityId, urlAlias } }) => [entityId ?? "", urlAlias ?? ""]) ?? [],
    [result],
  );

  useEffect(() => {
    onSyncVideos(videos);
  }, [onSyncVideos, videos]);

  if (!notEmpty(result)) return null;

  return <VideoUpNextWidget {...props} reference={result} />;
};

UpNextWidget.displayName = "UpNextWidget";
